import {z as r} from 'zod';

// NOTE: `fetchAndDeserialize` runs humps `camelizeKeys` on every response,
// so all schemas below describe the camelCased shape (e.g. `brandScore`, `byModel`,
// `scoreAlgoVersion`, `competitorRefId`, `maxPrompts`, `monthlyGrant`, `periodEnd`).

// --- config ---

const aiMonitoringPromptSchema = r.object({
    id: r.number(),
    text: r.string(),
    isActive: r.boolean(),
});

const aiMonitoringCompetitorConfigSchema = r.object({
    id: r.number(),
    name: r.string(),
    competitorRefId: r.number().nullable(),
    isActive: r.boolean(),
});

// GET /available-competitors/ -> plain JSON array [{id, name}]
const aiMonitoringAvailableCompetitorSchema = r.object({
    id: r.number(),
    name: r.string(),
});

export const aiMonitoringAvailableCompetitorsSchema = r.array(aiMonitoringAvailableCompetitorSchema);

export type AiMonitoringAvailableCompetitorType = r.infer<typeof aiMonitoringAvailableCompetitorSchema>;

const aiMonitoringLimitsSchema = r.object({
    maxPrompts: r.number(),
    maxCompetitors: r.number(),
    maxModels: r.number(),
});

const aiMonitoringPermissionsSchema = r.object({
    canManage: r.boolean(),
});

// Web-presence signals the brand-analysis pass checks; the user picks a subset in settings.
export enum AiMonitoringVisibilitySignalEnum {
    officialSite = 'official_site',
    socials = 'socials',
    directories = 'directories',
    reviews = 'reviews',
    media = 'media',
    communities = 'communities',
}

export enum AiMonitoringAspectModeEnum {
    auto = 'auto',
    custom = 'custom',
}

const aiMonitoringAspectModeSchema = r.nativeEnum(AiMonitoringAspectModeEnum);

// Max number of custom tonality aspects a user may pin (mirrors backend MAX_CUSTOM_ASPECTS).
export const AI_MONITORING_MAX_ASPECTS = 5;

// A custom tonality aspect pinned from the brand's review aspects.
const aiMonitoringCustomAspectSchema = r.object({
    tagId: r.number(),
    title: r.string(),
});

export const aiMonitoringConfigSchema = r.object({
    enabled: r.boolean(),
    language: r.string(),
    region: r.string(),
    scoreAlgoVersion: r.number(),
    prompts: r.array(aiMonitoringPromptSchema),
    competitors: r.array(aiMonitoringCompetitorConfigSchema),
    models: r.array(r.string()),
    visibilitySignals: r.array(r.nativeEnum(AiMonitoringVisibilitySignalEnum)),
    aspectMode: aiMonitoringAspectModeSchema,
    customAspects: r.array(aiMonitoringCustomAspectSchema),
    limits: aiMonitoringLimitsSchema,
    permissions: aiMonitoringPermissionsSchema,
});

export type AiMonitoringConfigType = r.infer<typeof aiMonitoringConfigSchema>;

// payload for PUT /config/ (sent with humps decamelizeKeys via `serialize`)
type AiMonitoringConfigUpdatePromptType = {
    id?: number;
    text: string;
    isActive?: boolean;
};

type AiMonitoringConfigUpdateCompetitorType = {
    id?: number;
    competitorRefId: number;
    isActive?: boolean;
};

type AiMonitoringConfigUpdateAspectType = {
    tagId: number;
    title: string;
};

export type AiMonitoringConfigUpdatePayloadType = {
    enabled: boolean;
    prompts: Array<AiMonitoringConfigUpdatePromptType>;
    competitors: Array<AiMonitoringConfigUpdateCompetitorType>;
    visibilitySignals: Array<AiMonitoringVisibilitySignalEnum>;
    aspectMode: AiMonitoringAspectModeEnum;
    customAspects: Array<AiMonitoringConfigUpdateAspectType>;
};

// 422 error body for config: {code, detail}
export const aiMonitoringConfigErrorSchema = r.object({
    code: r.enum([
        'limit_exceeded',
        'unsupported_model',
        'monitoring_not_configured',
        'unknown_competitor',
        'invalid_visibility_signals',
        'aspects_required',
        'too_many_aspects',
    ]),
    detail: r.string().optional(),
});

export type AiMonitoringConfigErrorType = r.infer<typeof aiMonitoringConfigErrorSchema>;

// --- runs ---

export enum AiMonitoringRunStatusEnum {
    queued = 'queued',
    running = 'running',
    success = 'success',
    partial = 'partial',
    failed = 'failed',
}

const aiMonitoringRunStatusSchema = r.nativeEnum(AiMonitoringRunStatusEnum);

// POST /runs/ -> 202
export const aiMonitoringRunStartSchema = r.object({
    runId: r.string(),
    status: aiMonitoringRunStatusSchema,
});

export type AiMonitoringRunStartType = r.infer<typeof aiMonitoringRunStartSchema>;

const aiMonitoringRunModelScoreSchema = r.object({
    model: r.string(),
    score: r.number(),
    delta: r.number().nullable(),
    status: r.string(),
});

const aiMonitoringRunCompetitorScoreSchema = r.object({
    id: r.number(),
    name: r.string(),
    score: r.number(),
    delta: r.number().nullable(),
});

export const aiMonitoringRunSchema = r.object({
    runId: r.string(),
    status: aiMonitoringRunStatusSchema,
    runDate: r.string().nullable().optional(),
    trigger: r.string().nullable().optional(),
    brandScore: r.number().nullable(),
    brandScoreDelta: r.number().nullable(),
    visibilityScore: r.number().nullable().optional(),
    visibilityScoreDelta: r.number().nullable().optional(),
    tonalityScore: r.number().nullable().optional(),
    tonalityScoreDelta: r.number().nullable().optional(),
    byModel: r.array(aiMonitoringRunModelScoreSchema),
    competitors: r.array(aiMonitoringRunCompetitorScoreSchema),
    skippedModels: r.array(r.string()),
    error: r.string().nullable().optional(),
});

export type AiMonitoringRunType = r.infer<typeof aiMonitoringRunSchema>;

// --- scores (dynamics) ---

const aiMonitoringScoreCompetitorPointSchema = r.object({
    id: r.number(),
    name: r.string(),
    score: r.number(),
});

const aiMonitoringScorePointSchema = r.object({
    date: r.string(),
    runId: r.string(),
    brandScore: r.number().nullable(),
    visibilityScore: r.number().nullable().optional(),
    tonalityScore: r.number().nullable().optional(),
    competitors: r.array(aiMonitoringScoreCompetitorPointSchema),
});

export type AiMonitoringScorePointType = r.infer<typeof aiMonitoringScorePointSchema>;

export const aiMonitoringScoresSchema = r.object({
    series: r.array(aiMonitoringScorePointSchema),
});

export type AiMonitoringScoresType = r.infer<typeof aiMonitoringScoresSchema>;

// --- answers (DRF paginated) ---

export enum AiMonitoringAnswerModelStatusEnum {
    pending = 'pending',
    ok = 'ok',
    skipped = 'skipped',
    error = 'error',
}

export enum AiMonitoringSentimentEnum {
    positive = 'positive',
    neutral = 'neutral',
    negative = 'negative',
}

const aiMonitoringAnswerModelStatusSchema = r.nativeEnum(AiMonitoringAnswerModelStatusEnum);

const aiMonitoringAnswerCompetitorSchema = r.object({
    name: r.string(),
    position: r.number().nullable().optional(),
    sentiment: r.nativeEnum(AiMonitoringSentimentEnum).nullable().optional(),
});

const aiMonitoringAnswerSchema = r.object({
    id: r.number(),
    promptId: r.number(),
    promptText: r.string(),
    model: r.string(),
    text: r.string(),
    brandMentioned: r.boolean().nullable(),
    position: r.number().nullable(),
    sentiment: r.nativeEnum(AiMonitoringSentimentEnum).nullable(),
    competitorsMentioned: r.array(aiMonitoringAnswerCompetitorSchema),
    modelStatus: aiMonitoringAnswerModelStatusSchema,
});

export type AiMonitoringAnswerType = r.infer<typeof aiMonitoringAnswerSchema>;

export const aiMonitoringAnswersSchema = r.object({
    count: r.number(),
    next: r.string().nullable().optional(),
    previous: r.string().nullable().optional(),
    results: r.array(aiMonitoringAnswerSchema),
});

export type AiMonitoringAnswersType = r.infer<typeof aiMonitoringAnswersSchema>;

export type AiMonitoringAnswersParametersType = {
    runId?: string;
    promptId?: number;
    page?: number;
    pageSize?: number;
};

// --- recommendations ---

export const aiMonitoringRecommendationsSchema = r.object({
    runId: r.string(),
    items: r.array(r.string()),
});

export type AiMonitoringRecommendationsType = r.infer<typeof aiMonitoringRecommendationsSchema>;

// --- scores query parameters ---

export type AiMonitoringScoresParametersType = {
    dateFrom?: string;
    dateTo?: string;
    granularity?: 'day';
};

// --- brand analysis (tab "Анализ бренда") ---

const aiMonitoringVisibilitySignalResultSchema = r.object({
    key: r.nativeEnum(AiMonitoringVisibilitySignalEnum),
    found: r.boolean(),
    sources: r.array(r.string()),
});

export enum AiMonitoringTonalityEnum {
    positive = 'positive',
    neutral = 'neutral',
    mixed = 'mixed',
    negative = 'negative',
}

const aiMonitoringTonalityAspectSchema = r.object({
    name: r.string(),
    // -100..100
    sentiment: r.number(),
    evidence: r
        .object({
            quote: r.string(),
            source: r.string(),
        })
        .nullable()
        .optional(),
});

const aiMonitoringPositioningItemSchema = r.object({
    text: r.string(),
    sources: r.array(r.string()),
});

export const aiMonitoringBrandAnalysisSchema = r.object({
    runId: r.string(),
    runDate: r.string().nullable().optional(),
    visibilityScore: r.number().nullable(),
    visibilityScoreDelta: r.number().nullable().optional(),
    tonalityScore: r.number().nullable(),
    tonalityScoreDelta: r.number().nullable().optional(),
    summary: r.string(),
    category: r.string(),
    audience: r.string(),
    visibility: r.object({
        signals: r.array(aiMonitoringVisibilitySignalResultSchema),
    }),
    tonality: r.object({
        overall: r.nativeEnum(AiMonitoringTonalityEnum),
        aspects: r.array(aiMonitoringTonalityAspectSchema),
    }),
    positioning: r.object({
        strengths: r.array(aiMonitoringPositioningItemSchema),
        weaknesses: r.array(aiMonitoringPositioningItemSchema),
    }),
    sources: r.array(r.string()),
});

export type AiMonitoringBrandAnalysisType = r.infer<typeof aiMonitoringBrandAnalysisSchema>;

// The custom-aspect picker reuses the existing review-aspects endpoint
// (`useReviewsAspects` in service/reviews/reviews-tags), which returns {ids, title}.
